"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormField,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@layer/ui/components";
import { DataTable, StandardFormItem } from "@layer/ui/patterns";
import { useSetAtom } from "jotai";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import type { ClientDTO } from "@/api/types/clients.types";
import type { MerchantDTO } from "@/api/types/merchants.types";
import { getSelectedMerchantsColumns } from "@/features/campaigns/forms/create-campaign-form/steps/set-reward-form-step/selected-merchants-table-columns";
import { AddClientMerchantsDrawer } from "@/features/merchants/drawers/add-client-merchants-drawer";
import { clientsAtom } from "@/stores/atoms/clients";
import { selectedClientIdAtom } from "@/stores/atoms/selected-client";
import {
	BudgetTimePeriod,
	CreateClientSchema,
	type CreateClientSchemaType,
	createClientDefaultValues,
} from "./create-client-form.schema";

type CreateClientFormProps = {
	onClose: () => void;
	client?: ClientDTO;
};

const CreateClientForm = ({ onClose, client }: CreateClientFormProps) => {
	const t = useTranslations("Features.clientProfile.createClient");
	const tFields = useTranslations(
		"Features.clientProfile.createClient.fields",
	);
	const router = useRouter();
	const setClients = useSetAtom(clientsAtom);
	const setSelectedClientId = useSetAtom(selectedClientIdAtom);

	const form = useForm<z.infer<CreateClientSchemaType>>({
		resolver: zodResolver(CreateClientSchema(tFields)),
		defaultValues: client
			? {
					clientName: client.name,
					clientLogo: null,
					budgetAmount: client.budget.amount.toString(),
					budgetTimePeriod: undefined,
					merchants: client.merchants,
				}
			: createClientDefaultValues,
		mode: "onChange",
	});

	const onSubmit = (data: z.infer<CreateClientSchemaType>) => {
		const logoUrl =
			data.clientLogo instanceof File
				? URL.createObjectURL(data.clientLogo)
				: (client?.logo ?? "");

		if (client) {
			setClients((prev) =>
				prev.map((c) =>
					c.clientId === client.clientId
						? {
								...c,
								name: data.clientName,
								logo: logoUrl,
								budget: {
									amount: parseFloat(data.budgetAmount),
									currency: c.budget.currency,
								},
								merchants: data.merchants as MerchantDTO[],
							}
						: c,
				),
			);
			onClose();
		} else {
			const newClientId = crypto.randomUUID();
			setClients((prev) => [
				...prev,
				{
					clientId: newClientId,
					name: data.clientName,
					logo: logoUrl,
					logoUploadedAt: new Date().toISOString().split("T")[0],
					budget: {
						amount: parseFloat(data.budgetAmount),
						currency: "GBP",
					},
					merchants: data.merchants as MerchantDTO[],
				},
			]);
			setSelectedClientId(newClientId);
			onClose();
			router.push("/client-profile");
		}
	};

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="flex flex-col h-full"
			>
				<div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
					<FormField
						control={form.control}
						name="clientName"
						render={({ field }) => (
							<StandardFormItem
								label={tFields("clientName.label")}
								gridColSpan={2}
							>
								<Input
									{...field}
									placeholder={tFields(
										"clientName.placeholder",
									)}
								/>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="clientLogo"
						render={({
							field: { onChange, value: _value, ...rest },
						}) => (
							<StandardFormItem
								label={tFields("clientLogo.label")}
								gridColSpan={2}
							>
								<input
									type="file"
									accept="image/*"
									onChange={(e) =>
										onChange(e.target.files?.[0] ?? null)
									}
									className="file:mr-3 file:rounded-sm file:border-0 file:bg-app-bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-app-text cursor-pointer w-full rounded-md border border-input-border bg-input-bg/30 px-3 py-1.5 text-sm text-app-text outline-none transition-colors"
									{...rest}
								/>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="budgetAmount"
						render={({ field }) => (
							<StandardFormItem
								label={tFields("budgetAmount.label")}
								gridColSpan={2}
							>
								<Input
									{...field}
									inputMode="numeric"
									pattern="[0-9]*"
									placeholder={tFields(
										"budgetAmount.placeholder",
									)}
									onChange={(e) => {
										field.onChange(
											e.target.value.replace(
												/[^0-9]/g,
												"",
											),
										);
									}}
								/>
							</StandardFormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="budgetTimePeriod"
						render={({ field }) => (
							<StandardFormItem
								label={tFields("budgetTimePeriod.label")}
								gridColSpan={2}
							>
								<Select
									value={field.value}
									onValueChange={field.onChange}
								>
									<SelectTrigger className="w-full data-[placeholder]:text-(--color-app-text-subtle)">
										<SelectValue
											placeholder={tFields(
												"budgetTimePeriod.placeholder",
											)}
										/>
									</SelectTrigger>
									<SelectContent>
										{Object.values(BudgetTimePeriod).map(
											(period) => (
												<SelectItem
													key={period}
													value={period}
												>
													{tFields(
														`budgetTimePeriod.options.${period}`,
													)}
												</SelectItem>
											),
										)}
									</SelectContent>
								</Select>
							</StandardFormItem>
						)}
					/>

					<div className="flex flex-col gap-4">
						<div className="flex items-start justify-between gap-4">
							<div className="flex flex-col gap-1">
								<h3 className="text-base font-medium">
									{t("merchants.title")}
								</h3>
								<p className="text-sm text-muted-foreground">
									{t("merchants.description")}
								</p>
							</div>
							<AddClientMerchantsDrawer
								onSave={(newMerchants) => {
									const current =
										(form.getValues(
											"merchants",
										) as MerchantDTO[]) ?? [];
									const existingIds = new Set(
										current.map((m) => m.merchantId),
									);
									form.setValue("merchants", [
										...current,
										...newMerchants.filter(
											(m) =>
												!existingIds.has(m.merchantId),
										),
									]);
								}}
							/>
						</div>
						<FormField
							control={form.control}
							name="merchants"
							render={({ field }) => {
								const merchants: MerchantDTO[] =
									field.value ?? [];
								if (merchants.length === 0) {
									return (
										<div className="flex h-20 items-center justify-center rounded-lg border border-dashed">
											<p className="text-sm text-muted-foreground">
												{t("merchants.empty")}
											</p>
										</div>
									);
								}
								return (
									<DataTable
										data={merchants}
										columns={getSelectedMerchantsColumns({
											t,
											onRemove: (merchantId) => {
												field.onChange(
													merchants.filter(
														(m) =>
															m.merchantId !==
															merchantId,
													),
												);
											},
										})}
										hasToolbar={false}
										showPagination={false}
									/>
								);
							}}
						/>
					</div>
				</div>
				<div className="border-t px-4 py-3 flex items-center justify-end gap-3 shrink-0">
					<Button variant="outline" type="button" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button type="submit">{t("saveAndContinue")}</Button>
				</div>
			</form>
		</Form>
	);
};

export { CreateClientForm };
